import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { bookingApi, PublicLinkInfo, AvailabilityDay, AvailabilitySlot } from '../services/api';
import { AAMEmbed } from '../components/AAMEmbed';
import { AAMCustomFormFields } from '../components/CustomFormFields';
import { useEmbedMode } from '../hooks/useEmbedMode';
import { useTheme } from '../hooks/useTheme';
import {
    Calendar,
    Clock,
    User,
    Mail,
    MessageSquare,
    Star,
    X,
    Check,
    ChevronRight,
    ChevronLeft,
    Sun,
    Moon,
    Globe,
    ArrowLeft,
    ExternalLink
} from 'lucide-react';
import { Footer } from '../components/Footer';
import { detectTimezone, TIMEZONES, getTimezoneLabel } from '../utils/timezones';

const DAYS_OF_WEEK = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const formatDateReadable = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const monthName = date.toLocaleDateString('en-US', { month: 'long' });
    const dayNum = date.getDate();
    const yearNum = date.getFullYear();

    const suffix = (d: number) => {
        if (d > 3 && d < 21) return 'th';
        switch (d % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    return `${monthName} ${dayNum}${suffix(dayNum)}, ${yearNum}`;
};

const formatDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
};

interface SelectedSlot extends AvailabilitySlot {
    date: string;
    day_name: string;
}

type Step = 'select' | 'form' | 'success';

export function PublicBookingPage() {
    const { slug } = useParams<{ slug: string }>();
    const { isEmbedded, notifyParentHeight } = useEmbedMode();

    const [link, setLink] = useState<PublicLinkInfo | null>(null);

    const { toggleTheme, isDark } = useTheme({
        defaultTheme: link?.default_theme || 'dark',
        storageKey: slug
    });
    const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
    const [timezone, setTimezone] = useState(() => detectTimezone());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [errorUserName, setErrorUserName] = useState<string | null>(null);

    const [step, setStep] = useState<Step>('select');
    const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);

    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>([]);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [customFields, setCustomFields] = useState<Record<string, any>>({});
    const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const initialMonth = useMemo(() => {
        if (availability.length > 0) {
            const firstAvailable = availability.find(d => d.slots.length > 0);
            if (firstAvailable) {
                const firstDate = new Date(firstAvailable.date + 'T00:00:00');
                return { year: firstDate.getFullYear(), month: firstDate.getMonth() };
            }
            const firstDate = new Date(availability[0].date + 'T00:00:00');
            return { year: firstDate.getFullYear(), month: firstDate.getMonth() };
        }
        const today = new Date();
        return { year: today.getFullYear(), month: today.getMonth() };
    }, [availability]);

    const [viewMonth, setViewMonth] = useState(initialMonth);

    // Local "today" string (YYYY-MM-DD). Must use local date components, not
    // toISOString() (which is UTC): for users west of UTC, UTC has already
    // rolled to tomorrow in the afternoon/evening, which would mark today as
    // "past" and incorrectly disable same-day booking.
    const todayStr = useMemo(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    }, []);

    useEffect(() => {
        notifyParentHeight();
    }, [step, selectedSlots.length, isLoading, notifyParentHeight]);

    useEffect(() => {
        setSelectedDate(null);
        setSelectedSlots([]);
        setStep('select');

        if (availability.length > 0) {
            const firstAvailable = availability.find(d => d.slots.length > 0);
            if (firstAvailable) {
                const firstDate = new Date(firstAvailable.date + 'T00:00:00');
                setViewMonth({ year: firstDate.getFullYear(), month: firstDate.getMonth() });
            } else {
                const firstDate = new Date(availability[0].date + 'T00:00:00');
                setViewMonth({ year: firstDate.getFullYear(), month: firstDate.getMonth() });
            }
        }
    }, [timezone, availability]);

    useEffect(() => {
        if (!slug) return;
        loadLink();
    }, [slug, timezone]);

    useEffect(() => {
        if (link?.name) {
            document.title = `AAM Let's Meet: ${link.name}`;
        }
        return () => {
            document.title = "AAM Let's Meet";
        };
    }, [link?.name]);

    useEffect(() => {
        if (step !== 'success' || !link?.confirmation_redirect_url || link.confirmation_redirect_delay === null) {
            return;
        }

        const delay = link.confirmation_redirect_delay;
        
        if (delay === 0) {
            window.location.href = link.confirmation_redirect_url;
            return;
        }

        setRedirectCountdown(delay ?? null);

        const timer = setInterval(() => {
            setRedirectCountdown(prev => {
                if (prev === null || prev <= 1) {
                    clearInterval(timer);
                    window.location.href = link.confirmation_redirect_url!;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [step, link?.confirmation_redirect_url, link?.confirmation_redirect_delay]);

    const loadLink = async () => {
        setIsLoading(true);
        setError(null);
        setErrorUserName(null);
        try {
            const data = await bookingApi.getLink(slug!, timezone);
            setLink(data.link);
            setAvailability(data.availability);
        } catch (err: any) {
            if (err.data?.user_name) {
                setErrorUserName(err.data.user_name);
                setError('Booking unavailable');
            } else {
                setError(err.message || 'Failed to load booking page');
                setErrorUserName(null); // Clear user name if not available
            }
        } finally {
            setIsLoading(false);
        }
    };

    const availableDatesSet = useMemo(() => {
        return new Set(availability.filter(d => d.slots.length > 0).map(d => d.date));
    }, [availability]);

    const dateRange = useMemo(() => {
        if (availability.length === 0) return { min: null, max: null };
        const dates = availability.map(d => d.date).sort();
        return { min: dates[0], max: dates[dates.length - 1] };
    }, [availability]);

    const getConfirmationMessage = () => {
        if (!link?.confirmation_message) {
            return isDirectBooking
                ? `A calendar invitation has been sent to ${email}`
                : `Your request has been sent to ${link?.user_name}. You'll receive an email when they confirm.`;
        }

        return link.confirmation_message
            .replace(/\{\{name\}\}/g, name)
            .replace(/\{\{email\}\}/g, email)
            .replace(/\{\{meeting_name\}\}/g, link.name || '')
            .replace(/\{\{host_name\}\}/g, link.user_name || '');
    };

    const calendarDays = useMemo(() => {
        const { year, month } = viewMonth;
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const startDayOfWeek = firstDayOfMonth.getDay();
        const daysInMonth = lastDayOfMonth.getDate();

        const days: { date: string; day: number; isCurrentMonth: boolean; isToday: boolean }[] = [];

        const prevMonth = new Date(year, month, 0);
        const prevMonthDays = prevMonth.getDate();
        for (let i = startDayOfWeek - 1; i >= 0; i--) {
            const day = prevMonthDays - i;
            const prevYear = month === 0 ? year - 1 : year;
            const prevMonthNum = month === 0 ? 11 : month - 1;
            const dateStr = `${prevYear}-${String(prevMonthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: false, isToday: false });
        }

        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: true, isToday: dateStr === todayStr });
        }

        const remainingDays = 42 - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            const nextYear = month === 11 ? year + 1 : year;
            const nextMonthNum = month === 11 ? 0 : month + 1;
            const dateStr = `${nextYear}-${String(nextMonthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: false, isToday: false });
        }

        return days;
    }, [viewMonth]);

    const slotsForSelectedDate = useMemo(() => {
        if (!selectedDate) return [];
        const day = availability.find(d => d.date === selectedDate);
        return day?.slots || [];
    }, [selectedDate, availability]);

    const selectedDayInfo = useMemo(() => {
        if (!selectedDate) return null;
        return availability.find(d => d.date === selectedDate);
    }, [selectedDate, availability]);

    const canGoPrev = dateRange.min ? (() => {
        const minDate = new Date(dateRange.min + 'T00:00:00');
        return viewMonth.year > minDate.getFullYear() ||
            (viewMonth.year === minDate.getFullYear() && viewMonth.month > minDate.getMonth());
    })() : false;

    const canGoNext = dateRange.max ? (() => {
        const maxDate = new Date(dateRange.max + 'T00:00:00');
        return viewMonth.year < maxDate.getFullYear() ||
            (viewMonth.year === maxDate.getFullYear() && viewMonth.month < maxDate.getMonth());
    })() : false;

    const goToPrevMonth = () => {
        setViewMonth(prev => {
            if (prev.month === 0) {
                return { year: prev.year - 1, month: 11 };
            }
            return { year: prev.year, month: prev.month - 1 };
        });
    };

    const goToNextMonth = () => {
        setViewMonth(prev => {
            if (prev.month === 11) {
                return { year: prev.year + 1, month: 0 };
            }
            return { year: prev.year, month: prev.month + 1 };
        });
    };

    const handleTimezoneChange = (newTz: string) => {
        setTimezone(newTz);
        setSelectedSlots([]);
        setSelectedDate(null);
    };

    const handleDateSelect = (date: string) => {
        const d = new Date(date + 'T00:00:00');
        setViewMonth({ year: d.getFullYear(), month: d.getMonth() });
        setSelectedDate(date);
    };

    const handleTimeSelect = (slot: AvailabilitySlot) => {
        if (!selectedDayInfo || !selectedDate) return;

        const slotWithDate: SelectedSlot = {
            ...slot,
            date: selectedDate,
            day_name: selectedDayInfo.day_name
        };

        if (isDirectBooking) {
            setSelectedSlots([slotWithDate]);
        } else {
            const exists = selectedSlots.some(s => s.start_time_utc === slot.start_time_utc);
            if (exists) {
                setSelectedSlots(prev => prev.filter(s => s.start_time_utc !== slot.start_time_utc));
            } else {
                if (link && selectedSlots.length >= link.max_proposals) {
                    alert(`You can only select up to ${link.max_proposals} time preferences`);
                    return;
                }
                setSelectedSlots(prev => [...prev, slotWithDate]);
            }
        }
    };

    const handleConfirmTime = () => {
        if (selectedSlots.length > 0) {
            setStep('form');
        }
    };

    const handleBackToSelect = () => {
        setStep('select');
    };

    const removeSlot = (index: number) => {
        setSelectedSlots(prev => prev.filter((_, i) => i !== index));
    };

    const moveSlot = (from: number, to: number) => {
        if (to < 0 || to >= selectedSlots.length) return;
        setSelectedSlots(prev => {
            const result = [...prev];
            const [removed] = result.splice(from, 1);
            result.splice(to, 0, removed);
            return result;
        });
    };

    const isDirectBooking = link?.booking_type === 'direct';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedSlots.length === 0 || !name.trim() || !email.trim()) return;

        setIsSubmitting(true);
        setCustomFieldErrors({});

        try {
            const customFieldsData = Object.keys(customFields).length > 0 ? customFields : undefined;

            if (isDirectBooking) {
                await bookingApi.directBook(slug!, {
                    requester_name: name.trim(),
                    requester_email: email.trim(),
                    message: message.trim() || undefined,
                    custom_fields: customFieldsData,
                    slot: {
                        start_time_utc: selectedSlots[0].start_time_utc,
                        end_time_utc: selectedSlots[0].end_time_utc
                    },
                    timezone
                });
            } else {
                await bookingApi.submit(slug!, {
                    requester_name: name.trim(),
                    requester_email: email.trim(),
                    message: message.trim() || undefined,
                    custom_fields: customFieldsData,
                    slots: selectedSlots.map(s => ({
                        start_time_utc: s.start_time_utc,
                        end_time_utc: s.end_time_utc
                    })),
                    timezone
                });
            }

            setStep('success');
        } catch (err: any) {
            if (err.errors && typeof err.errors === 'object') {
                setCustomFieldErrors(err.errors);
            }
            alert(err.message || 'Failed to submit booking');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getCurrentTimeDisplay = (tzId: string = timezone) => {
        try {
            const now = new Date();
            const timeStr = now.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: tzId
            });
            return timeStr.toLowerCase();
        } catch {
            return '';
        }
    };

    if (isLoading) {
        if (isEmbedded) {
            return (
                <div className="aam-embed" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="aam-loading">
                        <div className="aam-loading-spinner" />
                        <span>Loading...</span>
                    </div>
                </div>
            );
        }
        return (
            <div className="booking-page" style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh'
            }}>
                <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div className="spinner" />
                </div>
                <Footer />
            </div>
        );
    }

    if (error || !link) {
        const errorMessage = errorUserName 
            ? `Sorry, ${errorUserName} is not accepting bookings at this time.`
            : error || 'This booking link is no longer active.';

        if (isEmbedded) {
            return (
                <div className="aam-embed" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="aam-error-state">
                        <div className="aam-error-icon">
                            <Calendar size={48} />
                        </div>
                        <h2>Booking Unavailable</h2>
                        <p>{errorMessage}</p>
                    </div>
                </div>
            );
        }
        return (
            <div className="booking-page aam-full-page">
                <div className="aam-header">
                    <div className="aam-header-content">
                        <div className="aam-header-logo">
                            <img src={isDark ? "/logo.png" : "/light_logo.png"} alt="AAM Let's Meet" className="navbar-logo" />
                        </div>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>

                <div className="aam-page-content">
                    <div className="aam-error-state">
                        <div className="aam-error-icon">
                            <Calendar size={48} />
                        </div>
                        <h2>Booking Unavailable</h2>
                        <p>{errorMessage}</p>
                    </div>
                </div>

                <Footer />
            </div>
        );
    }

    if (isEmbedded && slug) {
        return (
            <AAMEmbed
                link={link}
                availability={availability}
                timezone={timezone}
                onTimezoneChange={handleTimezoneChange}
                slug={slug}
            />
        );
    }

    if (step === 'success') {
        return (
            <div className="booking-page aam-full-page">
                <div className="aam-header">
                    <div className="aam-header-content">
                        <div className="aam-header-logo">
                            <img src={isDark ? "/logo.png" : "/light_logo.png"} alt="AAM Let's Meet" className="navbar-logo" />
                        </div>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>

                <div className="aam-page-content">
                    <div className="aam-success">
                        <div className="aam-success-icon">
                            <Check size={48} />
                        </div>
                        <h2>{isDirectBooking ? 'You are scheduled' : 'Request submitted'}</h2>
                        <p>{getConfirmationMessage()}</p>
                        <div className="aam-success-details">
                            <div className="aam-success-detail">
                                <strong>{link.name}</strong>
                            </div>
                            {selectedSlots.map((slot, index) => (
                                <div key={slot.start_time_utc} className="aam-success-detail">
                                    {!isDirectBooking && <span className="aam-success-rank">#{index + 1}</span>}
                                    <Clock size={16} />
                                    <span>{formatDateDisplay(slot.date)}</span>
                                    <span>{slot.start_time_local} - {slot.end_time_local}</span>
                                </div>
                            ))}
                            <div className="aam-success-detail">
                                <Globe size={16} />
                                <span>{getTimezoneLabel(timezone)}</span>
                            </div>
                        </div>

                        {link.confirmation_resources && link.confirmation_resources.length > 0 && (
                            <div className="aam-success-resources">
                                <h4>
                                    Helpful Resources
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                    {link.confirmation_resources.map((resource: { label: string; url: string }, index: number) => (
                                        <a
                                            key={index}
                                            href={resource.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <ExternalLink size={14} />
                                            {resource.label}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {redirectCountdown !== null && redirectCountdown > 0 && (
                            <p style={{ 
                                marginTop: 'var(--space-4)', 
                                color: 'var(--color-gray-600)',
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                Redirecting in {redirectCountdown} second{redirectCountdown !== 1 ? 's' : ''}...
                            </p>
                        )}
                    </div>
                </div>

                <Footer />
            </div>
        );
    }

    if (step === 'form') {
        return (
            <div className="booking-page aam-full-page">
                <div className="aam-header">
                    <div className="aam-header-content">
                        <div className="aam-header-logo">
                            <img src={isDark ? "/logo.png" : "/light_logo.png"} alt="AAM Let's Meet" className="navbar-logo" />
                        </div>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>

                <div className="aam-page-content">
                    <div className="aam-embed aam-embed-fullpage">
                        <div className="aam-info-panel">
                            {link.logo_url && (
                                <img src={link.logo_url} alt="" className="aam-logo" />
                            )}
                            <div className="aam-user-name">{link.user_name}</div>
                            <h1 className="aam-event-title">{link.name}</h1>
                            <div className="aam-event-meta">
                                <Clock size={16} />
                                <span>{link.duration_minutes} min</span>
                            </div>
                            {link.description && (
                                <p className="aam-event-description">{link.description}</p>
                            )}

                            <div className="aam-selected-time">
                                <div className="aam-selected-time-header">
                                    {isDirectBooking ? 'Selected Time' : `Selected Times (${selectedSlots.length})`}
                                </div>
                                {selectedSlots.map((slot, index) => (
                                    <div key={slot.start_time_utc} className="aam-selected-time-item">
                                        {!isDirectBooking && (
                                            <span className="aam-selected-time-rank">
                                                {index === 0 && <Star size={12} />}
                                                #{index + 1}
                                            </span>
                                        )}
                                        <div className="aam-selected-time-date">
                                            {slot.day_name}, {formatDateReadable(slot.date)}
                                        </div>
                                        <div className="aam-selected-time-slot">
                                            {slot.start_time_local} - {slot.end_time_local}
                                        </div>
                                    </div>
                                ))}
                                <div className="aam-selected-time-tz">
                                    {getTimezoneLabel(timezone)}
                                </div>
                            </div>
                        </div>

                        <div className="aam-form-panel">
                            <button className="aam-back-btn" onClick={handleBackToSelect}>
                                <ArrowLeft size={16} />
                                Back
                            </button>

                            <h2 className="aam-form-title">Enter Details</h2>

                            <form onSubmit={handleSubmit} className="aam-form">
                                <div className="aam-form-group">
                                    <label className="aam-label">Name *</label>
                                    <div className="aam-input-wrapper">
                                        <User size={18} />
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="Your name"
                                            required
                                            className="aam-input"
                                        />
                                    </div>
                                </div>

                                <div className="aam-form-group">
                                    <label className="aam-label">Email *</label>
                                    <div className="aam-input-wrapper">
                                        <Mail size={18} />
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="your@email.com"
                                            required
                                            className="aam-input"
                                        />
                                    </div>
                                </div>

                                {link.custom_fields && link.custom_fields.length > 0 && (
                                    <AAMCustomFormFields
                                        fields={link.custom_fields}
                                        values={customFields}
                                        onChange={(fieldName, value) => {
                                            setCustomFields(prev => ({ ...prev, [fieldName]: value }));
                                            if (customFieldErrors[fieldName]) {
                                                setCustomFieldErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors[fieldName];
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        errors={customFieldErrors}
                                    />
                                )}

                                <div className="aam-form-group">
                                    <label className="aam-label">
                                        Please share anything that will help prepare for our meeting.
                                    </label>
                                    <div className="aam-input-wrapper textarea">
                                        <MessageSquare size={18} />
                                        <textarea
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder="Optional message..."
                                            rows={3}
                                            className="aam-textarea"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting || !name.trim() || !email.trim()}
                                    className="aam-submit-btn"
                                >
                                    {isSubmitting ? 'Scheduling...' : (isDirectBooking ? 'Schedule Event' : 'Request Meeting')}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        );
    }

    return (
        <div className="booking-page aam-full-page">
            <div className="aam-header">
                <div className="aam-header-content">
                    <div className="aam-header-logo">
                        <img src={isDark ? "/logo.png" : "/light_logo.png"} alt="AAM Let's Meet" className="navbar-logo" />
                    </div>
                    <button
                        onClick={toggleTheme}
                        className="theme-toggle"
                        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                    >
                        {isDark ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                </div>
            </div>

            <div className="aam-page-content">
                <div className="aam-embed aam-embed-fullpage">
                    <div className="aam-info-panel">
                        {link.logo_url && (
                            <img src={link.logo_url} alt="" className="aam-logo" />
                        )}
                        <div className="aam-user-name">{link.user_name}</div>
                        <h1 className="aam-event-title">{link.name}</h1>
                        <div className="aam-event-meta">
                            <Clock size={16} />
                            <span>{link.duration_minutes} min</span>
                        </div>
                        {!isDirectBooking && (
                            <div className="aam-event-meta">
                                <Star size={16} />
                                <span>Select up to {link.max_proposals} time{link.max_proposals > 1 ? 's' : ''}</span>
                            </div>
                        )}
                        {link.description && (
                            <p className="aam-event-description">{link.description}</p>
                        )}

                        {!isDirectBooking && selectedSlots.length > 0 && (
                            <div className="aam-selected-times-summary">
                                <div className="aam-selected-times-header">
                                    <Star size={14} />
                                    Your Preferences ({selectedSlots.length}/{link.max_proposals})
                                </div>
                                {selectedSlots.map((slot, index) => (
                                    <div key={slot.start_time_utc} className="aam-selected-time-row">
                                        <div className="aam-selected-time-info">
                                            <span className="aam-selected-time-rank">#{index + 1}</span>
                                            <div>
                                                <div className="aam-selected-time-date-sm">
                                                    {slot.day_name} {formatDateReadable(slot.date)}
                                                </div>
                                                <div className="aam-selected-time-time">
                                                    {slot.start_time_local}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="aam-selected-time-actions">
                                            {selectedSlots.length > 1 && (
                                                <>
                                                    <button
                                                        onClick={() => moveSlot(index, index - 1)}
                                                        disabled={index === 0}
                                                        className="aam-move-btn"
                                                        title="Move up"
                                                    >
                                                        <ChevronLeft size={14} style={{ transform: 'rotate(90deg)' }} />
                                                    </button>
                                                    <button
                                                        onClick={() => moveSlot(index, index + 1)}
                                                        disabled={index === selectedSlots.length - 1}
                                                        className="aam-move-btn"
                                                        title="Move down"
                                                    >
                                                        <ChevronLeft size={14} style={{ transform: 'rotate(-90deg)' }} />
                                                    </button>
                                                </>
                                            )}
                                            <button
                                                onClick={() => removeSlot(index)}
                                                className="aam-remove-btn"
                                                title="Remove"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    onClick={handleConfirmTime}
                                    className="aam-next-btn"
                                >
                                    Next
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="aam-booking-panel">
                        <div className={`aam-calendar-section ${selectedDate ? 'has-times' : ''}`}>
                            <h2 className="aam-section-title">Select a Date & Time</h2>

                            <div className="aam-calendar-header">
                                <button
                                    onClick={goToPrevMonth}
                                    disabled={!canGoPrev}
                                    className="aam-nav-btn"
                                    aria-label="Previous month"
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <span className="aam-month-title">
                                    {MONTH_NAMES[viewMonth.month]} {viewMonth.year}
                                </span>
                                <button
                                    onClick={goToNextMonth}
                                    disabled={!canGoNext}
                                    className="aam-nav-btn"
                                    aria-label="Next month"
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </div>

                            <div className="aam-weekdays">
                                {DAYS_OF_WEEK.map((day, i) => (
                                    <div key={i} className="aam-weekday">{day}</div>
                                ))}
                            </div>

                            <div className="aam-calendar-grid">
                                {calendarDays.map((day, i) => {
                                    const isAvailable = availableDatesSet.has(day.date);
                                    const isSelected = day.date === selectedDate;
                                    const isPast = day.date < todayStr;
                                    const isDisabled = !isAvailable || isPast;

                                    return (
                                        <button
                                            key={i}
                                            onClick={() => isAvailable && !isPast && handleDateSelect(day.date)}
                                            disabled={isDisabled}
                                            className={`aam-day ${day.isCurrentMonth ? 'current-month' : 'other-month'} ${isAvailable && !isPast ? 'available' : ''} ${isSelected ? 'selected' : ''} ${day.isToday ? 'today' : ''}`}
                                        >
                                            <span className="aam-day-number">{day.day}</span>
                                            {day.isToday && <span className="aam-today-dot" />}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="aam-timezone">
                                <span className="aam-timezone-label">Time zone</span>
                                <div className="aam-timezone-select">
                                    <Globe size={14} />
                                    <select
                                        value={timezone}
                                        onChange={(e) => handleTimezoneChange(e.target.value)}
                                    >
                                        {TIMEZONES.map(tz => (
                                            <option key={tz.value} value={tz.value}>
                                                {tz.label} ({getCurrentTimeDisplay(tz.value)})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className={`aam-times-panel ${selectedDate ? 'visible' : ''}`}>
                            {selectedDate && (
                                <>
                                    <h3 className="aam-times-title">
                                        {selectedDayInfo?.day_name}, {' '}
                                        {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                                    </h3>
                                    <div className="aam-times-list">
                                        {slotsForSelectedDate.map((slot, idx) => {
                                            const isSelected = selectedSlots.some(s => s.start_time_utc === slot.start_time_utc);
                                            const isCurrentSelection = isDirectBooking && isSelected;
                                            return (
                                                <div key={idx} className="aam-time-row">
                                                    <button
                                                        onClick={() => handleTimeSelect(slot)}
                                                        className={`aam-time-btn ${isSelected ? 'selected' : ''}`}
                                                    >
                                                        {slot.start_time_local}
                                                        {!isDirectBooking && isSelected && (
                                                            <Check size={16} className="aam-time-check" />
                                                        )}
                                                    </button>
                                                    {isCurrentSelection && (
                                                        <button
                                                            onClick={handleConfirmTime}
                                                            className="aam-confirm-btn"
                                                        >
                                                            Confirm
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
